import dayjs from "dayjs";

/** Statuses the backend uses to mark a posting as no longer accepting applications. */
const CLOSED_JOB_STATUSES = ["Closed", "Stopped", "Rejected"];

export interface JobDeadline {
	year?: number;
	month?: number;
	day?: number;
}

export interface JobStatusFields {
	jobStatus?: string;
	closeAt?: string;
	deadlineDate?: JobDeadline;
}

/** Normalise any deadline shape the API returns into a YYYY-MM-DD string. */
export function toDeadlineString(value?: string | Date | JobDeadline): string {
	if (!value) return "";

	if (typeof value === "string" || value instanceof Date) {
		const parsed = dayjs(value);
		return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
	}

	if (!value.year || !value.month || !value.day) return "";
	return dayjs(new Date(value.year, value.month - 1, value.day)).format("YYYY-MM-DD");
}

/**
 * A deadline counts as passed only once its whole day is over.
 *
 * closeAt carries a time (often 04:00 UTC), so comparing raw timestamps would retire a role
 * part-way through the very day the listing advertises. Candidates get the full day to apply.
 *
 * This is the single definition of "expired" — the row badge, the CTA label, the drawer, and
 * the open/closed split all read from here, so they cannot disagree with each other.
 */
export function isDeadlinePassed(value?: string | Date | JobDeadline): boolean {
	const deadline = toDeadlineString(value);
	return Boolean(deadline) && dayjs(deadline).isBefore(dayjs(), "day");
}

/** The date a job stops accepting applications, preferring deadlineDate over closeAt. */
export function getJobDeadline(job?: JobStatusFields): string {
	return toDeadlineString(job?.deadlineDate) || toDeadlineString(job?.closeAt);
}

/** A job is closed once the employer marks it closed, or once its deadline is in the past. */
export function isClosedJob(job?: JobStatusFields): boolean {
	if (job?.jobStatus && CLOSED_JOB_STATUSES.includes(job.jobStatus)) return true;

	const deadline = getJobDeadline(job);
	return Boolean(deadline) && dayjs(deadline).isBefore(dayjs(), "day");
}

export function isOpenJob(job?: JobStatusFields): boolean {
	return !isClosedJob(job);
}

/** Missing or unparseable deadlines sort last rather than poisoning the comparator with NaN. */
function deadlineValue(job: JobStatusFields): number {
	const deadline = getJobDeadline(job);
	if (!deadline) return 0;

	const parsed = dayjs(deadline);
	return parsed.isValid() ? parsed.valueOf() : 0;
}

/** How far back the "Recently Closed" section reaches. */
export const RECENTLY_CLOSED_MAX_AGE_DAYS = 30;

/** Most closed roles shown per company, so the section never buries the open ones. */
export const RECENTLY_CLOSED_MAX_JOBS = 10;

/**
 * Split a company's postings into the two sections a company page renders.
 *
 * Closed roles are limited to the last RECENTLY_CLOSED_MAX_AGE_DAYS, sorted most recently
 * closed first, then capped at RECENTLY_CLOSED_MAX_JOBS. A closed job with no usable
 * deadline is dropped rather than shown, since we cannot prove it closed recently.
 */
export function splitJobsByStatus<T extends JobStatusFields>(jobs: T[] = []): { openJobs: T[]; closedJobs: T[] } {
	const openJobs: T[] = [];
	const closedJobs: T[] = [];
	const cutoff = dayjs().subtract(RECENTLY_CLOSED_MAX_AGE_DAYS, "day").valueOf();

	for (const job of jobs) {
		if (!isClosedJob(job)) {
			openJobs.push(job);
			continue;
		}

		const closedOn = deadlineValue(job);
		if (closedOn && closedOn >= cutoff) {
			closedJobs.push(job);
		}
	}

	closedJobs.sort((a, b) => deadlineValue(b) - deadlineValue(a));

	return { openJobs, closedJobs: closedJobs.slice(0, RECENTLY_CLOSED_MAX_JOBS) };
}
